import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ValidationModel } from '../../../models/ValidationModel';
import { Office, useOffices, useZipcode } from '../../../utils';
import { FormContext } from '../../../store/MLI/MLIContext';
import { API } from '../../../services';
import { formItems, transformDataForAPI } from './form-items';
import { StepperContent } from '../../shared/Stepper';
import { Text } from '../../shared/Text';
import { Select } from '../../shared/Select';
import { InputHolder, InputHolderItem } from '../../shared/InputHolder';
import { Radio } from '../../shared/Radio';
import { Grid } from '../../shared/Grid';
import { Column } from '../../shared/Column';
import { Input } from '../../shared/Input';
import { isValidEmail, isValidPhoneNumber, isValidpostalCode } from '../../../utils/validations';
import { Textarea } from '../../shared/Textarea';
import { Button } from '../../shared/Button';
import { OfficeSelector } from '../../shared/OfficeSelector';
import { EmailCheckFormFieldsModel } from '../../../models/EmailCheckFormFieldsModel';

interface AppointmentFormProps {
    formData?: EmailCheckFormFieldsModel;
}

export const AppointmentForm: FunctionComponent<AppointmentFormProps> = ({ formData }) => {
    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors, isValid },
    } = useForm({ mode: 'onBlur' });

    const history = useHistory();
    const [appointmentErrors, setAppointmentErrors] = useState<ValidationModel | null>(null);
    const [officeFromPostalCode, setOfficeFromPostalCode] = useState<Office | null>(null);
    const { fetchZipcode, validZipcode } = useZipcode();
    const { fetchOfficeByZipcode } = useOffices();
    const { setIsLoading } = useContext(FormContext);

    useEffect(() => {
        if (!formData) return;
        setOfficeFromPostalCode(formData.office);
        setValue('name', formData.name);
        setValue('tussenvoegsel', formData.tussenvoegsel);
        setValue('lastname', formData.lastname);
        setValue('phoneNumber', formData.phoneNumber);
        setValue('email', formData.email);
    }, [formData, setValue]);

    const setOfficeValue = useCallback(
        (value) => {
            setValue('office', value, { shouldValidate: true });
        },
        [setValue],
    );

    const onSubmit = (data: any) => {
        if (!data.office) {
            if (
                window.confirm(
                    'Er is iets mis gegaan, probeer het opnieuw. Blijft dit probleem zich voordoen, neem dan contact op met info@hypotheker.nl.',
                )
            ) {
                window.location.reload();
            }
            return;
        }
        setIsLoading({ state: true, message: 'We zijn uw afspraak aan het maken' });
        //console.log(transformDataForAPI(data))
        API.post('clientcommunication/', transformDataForAPI(data))
            .then(() => history.push(`bedankt`))
            .finally(() => setIsLoading({ state: false, message: '' }))
            .catch((error: ValidationModel) => {
                setAppointmentErrors(error);
                //console.table(error);
            });
    };

    const resetValuesZipcode = (zipcode: string) => {
        setValue('zipcode', zipcode, { shouldValidate: true });
        setValue('streetname', '');
        setValue('city', '');
        setValue('office', '', { shouldValidate: true });
    };

    const getCityAndPlace = () => {
        const values = getValues();
        const zipcode = values[formItems.zipcode] as string;
        const housenumber = values[formItems.housenumber];

        if (zipcode && housenumber) {
            fetchOfficeByZipcode(zipcode).then((res) => {
                if (res.length) {
                    if (!values[formItems.office]) {
                        setValue('office', res[0]);
                        setOfficeFromPostalCode(res.length ? res[0] : null);
                    }
                }
            });

            fetchZipcode(zipcode, housenumber)
                .then((res) => {
                    const { streetname, city } = res;

                    if (streetname && city) {
                        setValue('streetname', streetname, { shouldValidate: true });
                        setValue('city', city, { shouldValidate: true });
                    } else {
                        resetValuesZipcode(zipcode);
                    }
                })
                .catch(() => {
                    resetValuesZipcode(zipcode);
                });
        }
    };

    return (
        <div className="u-padding-top--md">
            <StepperContent>
                <Text as="h3">Vrijblijvend gesprek met Hypotheekadviseur</Text>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="u-margin-bottom--md">
                        <Select
                            label="Reden voor afspraak*"
                            name={formItems.reason}
                            id={formItems.reason}
                            register={register}
                            validation={{ required: true }}
                            value="woning op het oog"
                            options={[
                                {
                                    value: 'oriënteren',
                                    name: 'oriënteren',
                                },
                                {
                                    value: 'woning op het oog',
                                    name: 'woning op het oog',
                                },
                                {
                                    value: 'woning gekocht',
                                    name: 'woning gekocht',
                                },
                                {
                                    value: 'hypotheek oversluiten',
                                    name: 'hypotheek oversluiten',
                                },
                                {
                                    value: 'tweede hypotheek',
                                    name: 'tweede hypotheek',
                                },
                                {
                                    value: 'einde rentevaste periode',
                                    name: 'einde rentevaste periode',
                                },
                                {
                                    value: 'andere vraag',
                                    name: 'andere vraag',
                                },
                            ]}
                        />
                    </div>

                    <hr />

                    <InputHolder>
                        <InputHolderItem isSmall={true}>
                            <Radio
                                label="Man"
                                checked={true}
                                name={formItems.gender}
                                id={`${formItems.gender}-1`}
                                defaultValue={'male'}
                                register={register}
                            />
                        </InputHolderItem>
                        <InputHolderItem isSmall={true}>
                            <Radio
                                label="Vrouw"
                                name={formItems.gender}
                                id={`${formItems.gender}-2`}
                                defaultValue={'female'}
                                register={register}
                            />
                        </InputHolderItem>
                    </InputHolder>

                    <div className="u-margin-top--md">
                        <Grid>
                            <Column cols={[6, null, null, 4]}>
                                <Input
                                    label="Voorletters*"
                                    name={formItems.name}
                                    id={formItems.name}
                                    register={register}
                                    hasError={errors[formItems.name]}
                                    validation={{ required: true }}
                                />
                                {errors[formItems.name] && (
                                    <span className="form__error">Dit veld is verplicht</span>
                                )}
                            </Column>
                            <Column cols={[6, null, null, 3]}>
                                <Input
                                    label="Tussenvoegsel"
                                    name={formItems.addition}
                                    id={formItems.addition}
                                    register={register}
                                    hasError={errors[formItems.addition]}
                                />
                            </Column>
                            <Column cols={[12, null, null, 5]}>
                                <Input
                                    label="Achternaam*"
                                    name={formItems.lastname}
                                    id={formItems.lastname}
                                    register={register}
                                    hasError={errors[formItems.lastname]}
                                    validation={{ required: true }}
                                />
                                {errors[formItems.lastname] && (
                                    <span className="form__error">Dit veld is verplicht</span>
                                )}
                            </Column>
                        </Grid>
                    </div>

                    <hr />

                    <div className="u-margin-top--md">
                        <Grid>
                            <Column cols={[7, null, null, 9]}>
                                <Input
                                    label="Postcode*"
                                    name={formItems.zipcode}
                                    id={formItems.zipcode}
                                    placeholder={'1234 AB'}
                                    register={register}
                                    hasError={errors[formItems.zipcode]}
                                    validation={{
                                        required: { value: true, message: 'Dit veld is verplicht' },
                                        validate: {
                                            validAdress:
                                                isValidpostalCode || 'Vul een geldige postcode in',
                                        },
                                        onBlur: getCityAndPlace,
                                    }}
                                />
                                {errors[formItems.zipcode] && (
                                    <span className="form__error">
                                        {errors[formItems.zipcode].message}
                                    </span>
                                )}
                            </Column>
                            <Column cols={[5, null, null, 3]}>
                                <Input
                                    label="Huisnummer*"
                                    name={formItems.housenumber}
                                    id={formItems.housenumber}
                                    register={register}
                                    validation={{
                                        required: true,
                                        onBlur: getCityAndPlace,
                                    }}
                                    hasError={errors[formItems.housenumber]}
                                />
                                {errors[formItems.housenumber] && (
                                    <span className="form__error">Dit veld is verplicht</span>
                                )}
                            </Column>
                        </Grid>
                    </div>

                    <div className={`${validZipcode ? 'u-hidden' : ''}`}>
                        <span className="u-text--info">
                            Vul hier je postcode in (1234 AB). Woon je in het buitenland? Voer hier
                            1234 AB in en vermeld de correcte postcode in het opmerkingenveld.
                        </span>
                        <InputHolder>
                            <InputHolderItem>
                                <Input
                                    label="Straatnaam*"
                                    name={formItems.streetname}
                                    id={formItems.streetname}
                                    register={register}
                                    hasError={errors[formItems.streetname]}
                                    validation={{
                                        required: true,
                                    }}
                                />
                                {errors[formItems.streetname] && (
                                    <span className="form__error">Dit veld is verplicht</span>
                                )}
                            </InputHolderItem>
                            <InputHolderItem>
                                <Input
                                    label="Plaatsnaam*"
                                    name={formItems.city}
                                    id={formItems.city}
                                    register={register}
                                    hasError={errors[formItems.city]}
                                    validation={{
                                        required: true,
                                    }}
                                />
                                {errors[formItems.city] && (
                                    <span className="form__error">Dit veld is verplicht</span>
                                )}
                            </InputHolderItem>
                        </InputHolder>
                    </div>

                    <hr />

                    <div className="u-margin-top--md">
                        <Input
                            label="Telefoonnummer*"
                            name={formItems.phoneNumber}
                            id={formItems.phoneNumber}
                            register={register}
                            hasError={errors[formItems.phoneNumber]}
                            type="tel"
                            helperLabel="Op dit nummer nemen we contact met je op."
                            validation={{ required: true, validate: isValidPhoneNumber }}
                        />
                        {errors[formItems.phoneNumber] && (
                            <span className="form__error">
                                Vul een tien-cijferig telefoonnummer in (voorbeeld: 06-12345678 of
                                010-1234567)
                            </span>
                        )}
                    </div>

                    <div className="u-margin-top--md">
                        <Input
                            label="E-mailadres*"
                            name={formItems.email}
                            id={formItems.email}
                            register={register}
                            hasError={errors[formItems.email]}
                            type="email"
                            helperLabel="Indien we je niet telefonisch kunnen bereiken sturen we je een e-mail."
                            validation={{ required: true, validate: isValidEmail }}
                        />
                        {errors[formItems.email] && (
                            <span className="form__error">
                                Vul een correct e-mailadres in (bijv. user@domain.com)
                            </span>
                        )}
                    </div>

                    <div className="u-margin-top--md">
                        <OfficeSelector office={officeFromPostalCode} setValue={setOfficeValue} />
                        <Input
                            name={formItems.office}
                            id={formItems.office}
                            register={register}
                            hasError={errors[formItems.office]}
                            type="hidden"
                            validation={{ required: true }}
                        />
                    </div>

                    <div className="u-margin-top--md">
                        <Textarea
                            label="Eventuele opmerkingen"
                            name={formItems.remarks}
                            id={formItems.remarks}
                            register={register}
                            hasError={errors[formItems.remarks]}
                            placeholder="Korte omschrijving hier"
                        />
                    </div>
                    {appointmentErrors?.errors &&
                        Object.values(appointmentErrors.errors).map((error, index) => (
                            <div key={index} className="u-margin-top--md">
                                <span className="form__error">{error}</span>
                            </div>
                        ))}
                    <div className="u-padding-top--md u-padding-bottom--md">
                        <Button label="Maak een afspraak" disabled={!isValid} />
                    </div>
                </form>
            </StepperContent>
        </div>
    );
};
